/**
 * `useSiloQuery` / `useInstanceQuery` — the two read wrappers every
 * `client/data` hook builds on.
 *
 * Every silo-scoped procedure takes the same three input fields: the silo, the
 * space for an instance-scoped read, and the locale to resolve user-written
 * text into. Each is ambient in the client — the datastore knows the silo and
 * the instance, and the language context knows the locale — so a hook that
 * threads them by hand repeats the same four lines and can forget the locale,
 * which would leave one cache entry serving every language.
 *
 * `TInput` and `TOutput` are inferred from the procedure util, so an `input`
 * that does not fit the chosen procedure is a compile error, and the injected
 * fields are omitted from what a caller may pass.
 */
import type { ProcedureUtils } from '@orpc/react-query';
import type { LocaleSelector } from '@repo/shared/orpc/translations';
import { type UseQueryOptions, type UseQueryResult, useQuery } from '@tanstack/react-query';
import { useSiloLanguage } from 'component/translation';
import { useInstanceKey, useSiloKey } from 'util/datastore';

type SiloInput = { siloKey: string; locale?: LocaleSelector };
type InstanceInput = SiloInput & { spaceKey: string };

/**
 * `options` is merged into the underlying `useQuery` call, for the few reads
 * that need TanStack behaviour the injection doesn't cover — `retry: false` on
 * one that reports a disabled feature through an error, say. Anything the
 * helper sets itself (the query key and function) is not overridable.
 */
type ReadOptions<TInput, TOutput> = {
    procedure: ProcedureUtils<any, TInput, TOutput, any>;
    enabled?: boolean;
    locale?: LocaleSelector;
    options?: Omit<UseQueryOptions<TOutput>, 'queryKey' | 'queryFn' | 'enabled'>;
};

export function useSiloQuery<TInput extends SiloInput, TOutput>({
    procedure,
    input,
    enabled = true,
    locale,
    options,
}: ReadOptions<TInput, TOutput> & { input?: Omit<TInput, 'siloKey' | 'locale'> }): UseQueryResult<TOutput> {
    const siloKey = useSiloKey();
    const currentLocale = useSiloLanguage();
    // The cast is what `Omit` costs: TypeScript can't see that putting the
    // three injected keys back onto `Omit<TInput, …>` reconstitutes `TInput`.
    // `usePaginatedQuery` has always done the same.
    const fullInput = { ...input, siloKey, locale: locale ?? currentLocale } as TInput;
    return useQuery({ ...options, ...procedure.queryOptions({ input: fullInput }), enabled });
}

/**
 * Gates the request on the instance key as well as the caller's `enabled`, so a
 * read rendered outside a space never fires with an empty key.
 */
export function useInstanceQuery<TInput extends InstanceInput, TOutput>({
    procedure,
    input,
    enabled = true,
    locale,
    options,
}: ReadOptions<TInput, TOutput> & { input?: Omit<TInput, 'siloKey' | 'spaceKey' | 'locale'> }): UseQueryResult<TOutput> {
    const siloKey = useSiloKey();
    const spaceKey = useInstanceKey();
    const currentLocale = useSiloLanguage();
    const fullInput = { ...input, siloKey, spaceKey, locale: locale ?? currentLocale } as TInput;
    return useQuery({ ...options, ...procedure.queryOptions({ input: fullInput }), enabled: enabled && !!spaceKey });
}
